<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <div class="page-header-with-filters">
      <d-row
        no-gutters
        class="page-header py-4">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">
            {{ $t('purchase.ordersFromSuppliers') }}
          </span>
          <h3 class="page-title">
            {{ $t('purchase.reservation.listOfReservationsFormOrdersWithSuppliers') }}
          </h3>
        </d-col>
      </d-row>

      <list-filters
        :config="filtersConfig"
        :isListLoaded="allDataLoaded"
        :view-name="'purchases-reservations'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('product.product') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px;">{{ $t('ui.order') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px">{{ $t('purchase.purchase') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 150px;">{{ $t('ui.amount') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(reservation, index) of reservations"
            :key="'reservation-' + index">
            <td class="align-middle">
              {{ reservation.product.name }}<br>
              <small>{{ $t('product.productCode') }}: <strong>{{ reservation.product.code }}</strong></small>
            </td>
            <td class="align-middle">
              <template v-if="reservation.order">
                <router-link :to="'/zamowienia/edycja/' + reservation.order.id + '/z-rezerwacji-zakupow'">
                  {{ reservation.order.order_name }}
                </router-link>
              </template>
            </td>
            <td class="align-middle">
              <router-link :to="'/zakupy/edycja/' + reservation.purchase_product.id + '/z-rezerwacji-zakupow'">
                {{ reservation.purchase_product.customer_name }} (#{{ reservation.purchase_product.id }})
              </router-link>
              <br>
              <small v-if="reservation.purchase_product.date">
                {{ $t('ui.deliveryDate') }}:
                <strong>
                  {{ reservation.purchase_product.date }}
                </strong>
              </small>
            </td>
            <td class="align-middle">
              {{ reservation.purchase_quantity }} {{ $t('ui.piecesAbbr') }}
            </td>
          </tr>
          <tr v-if="reservations.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('purchase.reservation.noPurchasesReservationsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'purchases-reservations',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedPurchases && this.loadedProducts;
    },
    productOptions () {
      let options = [];

      for (let i = 0; i < this.products.length; i++) {
        options.push({
          text: this.products[i].code,
          value: this.products[i].id
        });
      }

      return options;
    },
    purchaseOptions () {
      let options = [];

      for (let i = 0; i < this.purchases.length; i++) {
        let label = this.purchases[i].customer_name;
        if (this.purchases[i].date) {
          label += ` (${this.purchases[i].date})`;
        }

        options.push({
          text: label,
          value: this.purchases[i].id
        });
      }

      return options;
    },
    purchaseStatusOptions () {
      return [
        {
          text: this.$t('purchase.new'),
          value: 'NEW'
        },
        {
          text: this.$t('purchase.awaitingPayment'),
          value: 'WAITING_PAYMENT'
        },
        {
          text: this.$t('purchase.awaitingProducts'),
          value: 'WAITING_PRODUCTS'
        },
        {
          text: this.$t('purchase.finished'),
          value: 'CLOSED'
        }
      ];
    },
    filtersConfig () {
      return [
        {
          key: 'order',
          apiKey: 'order',
          label: this.$t('ui.order'),
          placeholder: this.$t('order.enterOrderNumber'),
          type: 'text',
          isBasic: true,
          defaultValue: ''
        },
        {
          key: 'product_id',
          apiKey: 'product_id',
          label: this.$t('product.selectProduct'),
          placeholder: this.$t('product.selectProduct'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.productOptions
        },
        {
          key: 'purchase_id',
          apiKey: 'purchase_id',
          label: this.$t('purchase.selectPurchase'),
          placeholder: this.$t('purchase.selectPurchase'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.purchaseOptions
        },
        {
          key: 'purchase_status',
          apiKey: 'purchase_status',
          label: this.$t('purchase.purchaseStatus'),
          type: 'select',
          isBasic: true,
          defaultValue: 'NEW',
          options: this.purchaseStatusOptions
        }
      ];
    }
  },
  data () {
    return {
      reservations: [],
      loaded: false,
      loadedPurchases: false,
      loadedProducts: false,
      loadError: false,
      purchases: [],
      products: [],
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      if (!where.purchase_status) {
        where.purchase_status = 'NEW';
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/purchases/reservations?page=' + this.currentPage,
        listField: 'reservations',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/purchases/items?return=get&where[status][]=NEW&where[status][]=WAITING_PAYMENTS&where[status][]=WAITING_PRODUCTS',
        loadedKey: 'loadedPurchases',
        outputKey: 'purchases',
        errorKey: 'loadError'
      });
    }
  }
};
</script>
